import { motion } from "framer-motion";

export default function IntelLayer() {
  return (
    <section
      id="dashboard"
      data-testid="intel-layer-section"
      className="relative py-12 lg:py-16 border-t border-white/[0.06] overflow-hidden scroll-mt-24"
    >
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-[820px] mb-14 lg:mb-16"
        >
          <div className="font-mono-label text-[#D4FF4A]/70 mb-6">
            Section 7 · The intelligence layer
          </div>
          <h2
            data-testid="intel-layer-heading"
            className="font-display text-white text-[44px] lg:text-[72px] font-semibold leading-[1.02] tracking-[-0.03em] mb-6"
          >
            Marcus is page one.{" "}
            <span className="text-[#71717A]">Here's the rest of the report.</span>
          </h2>
          <p className="text-lg text-[#A1A1AA] leading-[1.55] max-w-[680px]">
            Four focused views. Segments ranked. Channels mapped. Decisions walked
            backwards. Gaps flagged before your competitor sees them.
          </p>
        </motion.div>

        <div className="space-y-20 lg:space-y-28">
          <Chapter
            num="01"
            label="Personas"
            title="Four archetypes. One ranked list."
            body="Every buyer gets sorted into an archetype with a named human at the top. The Margin Maximizer. The Velocity Founder. The One-Person Brand. The Quota Carrier. You see who spends, how they think, and who you want more of."
            Visual={PersonasVisual}
            reversed={false}
            index={0}
          />
          <Chapter
            num="02"
            label="Signal map"
            title="Where they actually show up."
            body="The channels each segment reads, the creators they follow, the communities they lurk in. Plotted per segment, by volume. No 'self-reported' survey data. This is where attention actually is."
            Visual={SignalMapVisual}
            reversed={true}
            index={1}
          />
          <Chapter
            num="03"
            label="Buyer journey"
            title="How they actually decide."
            body="Awareness, consideration, decision. Told in their own words, pulled from interviews with the humans who bought. No stock funnel. The real question they ask at 2AM before they pull a credit card."
            Visual={JourneyVisual}
            reversed={false}
            index={2}
          />
          <Chapter
            num="04"
            label="Opportunity gap"
            title="Hidden growth opportunities."
            body="Segments ranked by LTV against buyer count. Highest value, lowest penetration, already flagged. Your next growth play sits at the top of the list, not buried in a slide deck."
            Visual={OpportunityVisual}
            reversed={true}
            index={3}
          />
        </div>
      </div>
    </section>
  );
}

function Chapter({ num, label, title, body, Visual, reversed, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
      data-testid={`intel-chapter-${index}`}
    >
      <div
        className={`lg:col-span-5 ${reversed ? "lg:col-start-8 lg:row-start-1" : ""}`}
      >
        <div className="flex items-baseline gap-5 mb-7">
          <span className="font-display text-[#D4FF4A] text-[88px] lg:text-[120px] font-semibold leading-none tracking-[-0.04em] hero-glow-soft">
            {num}
          </span>
          <span className="font-mono-label text-[#71717A] text-[11px]">
            {label}
          </span>
        </div>
        <h3 className="font-display text-white text-[32px] lg:text-[44px] font-semibold leading-[1.05] tracking-[-0.025em] mb-5 max-w-[440px]">
          {title}
        </h3>
        <p className="text-[#A1A1AA] text-[16px] lg:text-[17px] leading-[1.6] max-w-[440px]">
          {body}
        </p>
      </div>
      <div
        className={`lg:col-span-7 ${reversed ? "lg:col-start-1 lg:row-start-1" : ""}`}
      >
        <Visual />
      </div>
    </motion.div>
  );
}

function VisualShell({ children, label }) {
  return (
    <div className="relative bg-[#05060A] border border-white/[0.08] rounded-2xl overflow-hidden shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)]">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.05] bg-white/[0.015]">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-white/10" />
          <div className="w-2 h-2 rounded-full bg-white/10" />
          <div className="w-2 h-2 rounded-full bg-white/10" />
        </div>
        <div className="font-mono text-[#52525B] text-[10px] tracking-[0.12em]">{label}</div>
        <div className="w-12" />
      </div>
      <div className="p-6 lg:p-8">{children}</div>
    </div>
  );
}

function PersonasVisual() {
  return (
    <VisualShell label="dashboard / personas">
      <div className="font-mono-label text-[#52525B] text-[9px] mb-4">
        4 buyer segments · ranked by lifetime value
      </div>
      <div className="flex items-center gap-5 border-b border-white/[0.06] pb-3 mb-7 overflow-x-auto">
        <div className="flex items-center gap-2 whitespace-nowrap pb-2 border-b-2 border-[#D4FF4A] -mb-[9px]">
          <span className="font-display text-[#D4FF4A] text-[14px] font-semibold">
            Agency Owner
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#D4FF4A]/15 border border-[#D4FF4A]/30 text-[#D4FF4A] text-[8px] font-mono tracking-[0.16em]">
            ★ CORE
          </span>
        </div>
        <span className="font-display text-[#52525B] text-[13px] whitespace-nowrap">
          SaaS / Founder
        </span>
        <span className="font-display text-[#52525B] text-[13px] whitespace-nowrap">
          Creator
        </span>
        <span className="font-display text-[#52525B] text-[13px] whitespace-nowrap hidden sm:inline">
          In-house Marketer
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-6">
        <div>
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/25 mb-4">
            <span className="font-mono text-[#D4FF4A] text-[10px] tracking-[0.14em] uppercase">
              Agency Owner / Client Services
            </span>
          </div>
          <h4 className="font-display text-white text-[28px] lg:text-[32px] font-semibold leading-[1.05] mb-2">
            The Margin Maximizer
          </h4>
          <p className="text-[#A1A1AA] text-[13px] mb-6 max-w-[420px] leading-[1.5]">
            Trades time for money and is obsessed with making that exchange more efficient.
          </p>
          <div className="grid grid-cols-4 gap-3">
            <PersonaStat v="236" l="Verified" />
            <PersonaStat v="27%" l="Of cohort" />
            <PersonaStat v="$2,878" l="Avg LTV" />
            <PersonaStat v="3rd" l="LTV rank" />
          </div>
        </div>

        <div className="bg-[#0A0B10] border border-white/[0.06] rounded-xl p-4">
          <div className="font-mono-label text-[#71717A] text-[9px] mb-4">
            Real buyers · segment
          </div>
          <div className="space-y-3">
            <BuyerRow
              initials="DR"
              name="Dominic Reyes"
              ltv="$25,891"
              meta="Brooklyn · Cadence Studio"
            />
            <BuyerRow
              initials="NP"
              name="Naomi Patel"
              ltv="$19,385"
              meta="Denver · Fractional COO"
            />
            <BuyerRow
              initials="LV"
              name="Lucas Vance"
              ltv="$19,339"
              meta="Nashville · Lead Gen Agency"
            />
          </div>
          <div className="flex items-center gap-3 pt-3 mt-3 border-t border-white/[0.06]">
            <div className="w-7 h-7 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/25 flex items-center justify-center shrink-0">
              <span className="font-mono text-[#D4FF4A] text-[9px] font-semibold">+5</span>
            </div>
            <div>
              <div className="text-white text-[12px] font-medium">5 more</div>
              <div className="text-[#71717A] text-[10px]">US · UK · IT · CH</div>
            </div>
          </div>
        </div>
      </div>
    </VisualShell>
  );
}

function PersonaStat({ v, l }) {
  return (
    <div>
      <div className="font-display text-[#D4FF4A] text-[22px] lg:text-[24px] font-semibold leading-none mb-1.5 tracking-tight">
        {v}
      </div>
      <div className="font-mono text-[#71717A] text-[9px] tracking-[0.12em] uppercase">
        {l}
      </div>
    </div>
  );
}

function BuyerRow({ initials, name, ltv, meta }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/25 flex items-center justify-center shrink-0 mt-0.5">
        <span className="font-mono text-[#D4FF4A] text-[9px] font-semibold">{initials}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-[13px] font-medium leading-tight mb-0.5 truncate">
          {name}
        </div>
        <div className="text-[#D4FF4A] text-[11px] font-mono leading-none mb-1">
          {ltv} LTV
        </div>
        <div className="text-[#71717A] text-[10px] leading-tight truncate">{meta}</div>
      </div>
    </div>
  );
}

function SignalMapVisual() {
  const axes = [
    "LinkedIn",
    "Blog & Content",
    "Newsletters",
    "Conferences",
    "Community",
    "Podcasts",
    "YouTube",
    "X",
  ];
  const segments = [
    { name: "Agency Owner", color: "#D4FF4A", values: [9, 8, 3, 2, 3, 2, 2, 4], opacity: 0.35, active: true },
    { name: "SaaS / Founder", color: "#5EA9FF", values: [6, 4, 5, 3, 4, 3, 3, 6], opacity: 0.12 },
    { name: "Creator", color: "#E879F9", values: [5, 3, 2, 1, 2, 3, 9, 4], opacity: 0.12 },
    { name: "In-house Marketer", color: "#4ADE80", values: [7, 6, 7, 4, 3, 2, 3, 3], opacity: 0.12 },
  ];

  const size = 360;
  const center = size / 2;
  const maxR = size / 2 - 54;
  const n = axes.length;

  const angleOf = (i) => (i * 2 * Math.PI) / n - Math.PI / 2;
  const polyPoints = (values) =>
    values
      .map((v, i) => {
        const r = (v / 10) * maxR;
        const a = angleOf(i);
        return `${center + r * Math.cos(a)},${center + r * Math.sin(a)}`;
      })
      .join(" ");

  return (
    <VisualShell label="dashboard / signal map">
      <div className="font-mono-label text-[#52525B] text-[9px] mb-5">
        Content consumption · by segment · indexed 0-10
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 items-center">
        <div className="space-y-3">
          {segments.map((s, idx) => (
            <div key={s.name} className="flex items-center gap-2.5">
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: s.color, boxShadow: idx === 0 ? `0 0 12px ${s.color}` : "none" }}
              />
              <span
                className="text-[13px]"
                style={{
                  color: idx === 0 ? "#FFF" : "#A1A1AA",
                  fontWeight: idx === 0 ? 600 : 400,
                }}
              >
                {s.name}
              </span>
            </div>
          ))}
          <div className="pt-5 border-t border-white/[0.06] mt-5">
            <div className="font-mono-label text-[#71717A] text-[9px] mb-2.5">
              Who they follow
            </div>
            <div className="text-[#D4FF4A] text-[12px] font-semibold mb-2 font-mono">
              LINKEDIN
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["Rand Fishkin", "Wil Reynolds", "Jason Lemkin", "Talia Wolf", "Chris Do"].map(
                (p) => (
                  <span
                    key={p}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[#D4D4D8] text-[10px]"
                  >
                    {p}
                  </span>
                ),
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center min-h-[360px]">
          <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[360px] h-auto">
            {[0.25, 0.5, 0.75, 1.0].map((k) => (
              <polygon
                key={k}
                points={axes
                  .map((_, i) => {
                    const r = k * maxR;
                    const a = angleOf(i);
                    return `${center + r * Math.cos(a)},${center + r * Math.sin(a)}`;
                  })
                  .join(" ")}
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
              />
            ))}
            {axes.map((_, i) => {
              const a = angleOf(i);
              return (
                <line
                  key={i}
                  x1={center}
                  y1={center}
                  x2={center + maxR * Math.cos(a)}
                  y2={center + maxR * Math.sin(a)}
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth="1"
                />
              );
            })}
            {segments
              .filter((s) => !s.active)
              .map((s) => (
                <polygon
                  key={s.name}
                  points={polyPoints(s.values)}
                  fill={s.color}
                  fillOpacity={s.opacity}
                  stroke={s.color}
                  strokeOpacity="0.45"
                  strokeWidth="1"
                />
              ))}
            {segments
              .filter((s) => s.active)
              .map((s) => (
                <polygon
                  key={s.name}
                  points={polyPoints(s.values)}
                  fill={s.color}
                  fillOpacity={s.opacity}
                  stroke={s.color}
                  strokeWidth="2"
                  style={{ filter: `drop-shadow(0 0 10px ${s.color}66)` }}
                />
              ))}
            {axes.map((label, i) => {
              const a = angleOf(i);
              const r = maxR + 24;
              const x = center + r * Math.cos(a);
              const y = center + r * Math.sin(a);
              const cos = Math.cos(a);
              const anchor = cos > 0.2 ? "start" : cos < -0.2 ? "end" : "middle";
              return (
                <text
                  key={label}
                  x={x}
                  y={y}
                  fill="#71717A"
                  fontSize="10"
                  fontFamily="JetBrains Mono, monospace"
                  textAnchor={anchor}
                  dominantBaseline="middle"
                  style={{ letterSpacing: "0.04em" }}
                >
                  {label}
                </text>
              );
            })}
          </svg>
        </div>
      </div>
    </VisualShell>
  );
}

function JourneyVisual() {
  const stages = [
    {
      stage: "AWARENESS",
      sub: "First touch · ToFU",
      width: 100,
      bullets: [
        "Deal discovery starts in the inbox. AppSumo digest is a ritual.",
        "LinkedIn surfaces peer proof from other agency owners in real time.",
      ],
    },
    {
      stage: "CONSIDERATION",
      sub: "Research · MoFU",
      width: 82,
      bullets: [
        "Peer credibility collapses weeks of research into a same-day decision.",
        "They read every review to find another agency owner who used it.",
      ],
    },
    {
      stage: "DECISION",
      sub: "Converts · BoFU",
      width: 62,
      bullets: [
        "Lifetime deal math closes them. Annual sub cost vs. one-time price.",
        "'Can I charge a client for this?' is the unspoken close.",
      ],
    },
  ];
  return (
    <VisualShell label="dashboard / buyer journey">
      <div className="font-mono-label text-[#52525B] text-[9px] mb-6">
        Purchase decision funnel · agency owner segment
      </div>
      <div className="space-y-5">
        {stages.map((s, i) => (
          <div key={s.stage} className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-5 items-center">
            <div className="flex items-center justify-center">
              <FunnelStage width={s.width} stage={s.stage} sub={s.sub} />
            </div>
            <div className="space-y-2.5 border-l border-white/[0.06] pl-5">
              {s.bullets.map((b, j) => (
                <div key={j} className="flex items-start gap-2.5">
                  <span className="text-[#D4FF4A] text-[13px] leading-[1.5] shrink-0 font-mono">
                    →
                  </span>
                  <p className="text-[#D4D4D8] text-[13px] leading-[1.55]">{b}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </VisualShell>
  );
}

function FunnelStage({ width, stage, sub }) {
  const W = 200;
  const H = 68;
  const outer = W * (width / 100);
  const inner = outer - 20;
  const xLeft = (W - outer) / 2;
  const xRight = W - xLeft;
  const xInnerLeft = (W - inner) / 2;
  const xInnerRight = W - xInnerLeft;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }}>
      <defs>
        <linearGradient id={`stageGrad-${stage}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(212, 255, 74, 0.18)" />
          <stop offset="100%" stopColor="rgba(212, 255, 74, 0.08)" />
        </linearGradient>
      </defs>
      <polygon
        points={`${xLeft},0 ${xRight},0 ${xInnerRight},${H} ${xInnerLeft},${H}`}
        fill={`url(#stageGrad-${stage})`}
        stroke="rgba(212, 255, 74, 0.55)"
        strokeWidth="1.25"
      />
      <text
        x={W / 2}
        y={32}
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="12"
        fontWeight="700"
        fontFamily="Inter Tight, sans-serif"
        style={{ letterSpacing: "0.14em" }}
      >
        {stage}
      </text>
      <text
        x={W / 2}
        y={49}
        textAnchor="middle"
        fill="#71717A"
        fontSize="9"
        fontFamily="JetBrains Mono, monospace"
        style={{ letterSpacing: "0.06em" }}
      >
        {sub}
      </text>
    </svg>
  );
}

function OpportunityVisual() {
  const cards = [
    {
      label: "Creator",
      badge: "BIGGEST OPP",
      color: "#E879F9",
      value: "$3,455",
      rank: "#1 avg LTV",
      buyers: "65",
      pct: "7%",
      desc: "3.6x fewer than agency owners but spend 20% more. Market share gap, not preference.",
    },
    {
      label: "In-house Marketer",
      badge: "UNDERSERVED",
      color: "#4ADE80",
      value: "$3,160",
      rank: "#2 avg LTV",
      buyers: "145",
      pct: "18%",
      desc: "Second highest LTV, only 18% of buyers. High value with room to grow.",
    },
    {
      label: "Agency Owner",
      badge: "ESTABLISHED",
      color: "#D4FF4A",
      value: "$2,878",
      rank: "#3 avg LTV",
      buyers: "236",
      pct: "27%",
      desc: "Assumed core buyer. Data shows 3rd in LTV. Still valuable, not the top avg.",
      isCore: true,
    },
    {
      label: "SaaS / Founder",
      badge: "SATURATED",
      color: "#5EA9FF",
      value: "$2,763",
      rank: "#4 · lowest",
      buyers: "397",
      pct: "47%",
      desc: "Most buyers, lowest LTV. Optimize retention, not more acquisition.",
    },
  ];
  return (
    <VisualShell label="dashboard / opportunity gap">
      <div className="font-mono-label text-[#52525B] text-[9px] mb-2">
        Growth opportunity signal
      </div>
      <h4 className="font-display text-white text-[22px] lg:text-[26px] font-semibold mb-2 tracking-tight">
        Highest value · lowest penetration
      </h4>
      <div className="text-[#71717A] text-[11px] mb-6">
        Segments ranked by avg LTV vs. buyer count. The gap is where the money is.
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            className="relative bg-[#0A0B10] border rounded-xl p-4 overflow-hidden"
            style={{ borderColor: `${c.color}40` }}
          >
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: c.color, boxShadow: `0 0 20px ${c.color}80` }}
            />
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="font-mono text-[9px] tracking-[0.14em] uppercase truncate"
                  style={{ color: c.color }}
                >
                  {c.label}
                </span>
                {c.isCore && <span style={{ color: c.color }}>★</span>}
              </div>
              <span
                className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[8px] tracking-[0.12em] uppercase"
                style={{
                  color: c.color,
                  background: `${c.color}14`,
                  border: `1px solid ${c.color}30`,
                }}
              >
                {c.badge}
              </span>
            </div>
            <div
              className="font-display text-[32px] lg:text-[36px] font-bold leading-none mb-1 tracking-tight"
              style={{ color: c.color, textShadow: `0 0 24px ${c.color}40` }}
            >
              {c.value}
            </div>
            <div className="text-[#71717A] text-[10px] mb-3 font-mono">{c.rank}</div>
            <div className="flex items-baseline gap-1.5 mb-3">
              <span className="text-white text-[14px] font-semibold">{c.buyers}</span>
              <span className="text-[#71717A] text-[10px]">buyers · {c.pct}</span>
            </div>
            <div className="h-px bg-white/[0.06] mb-3" />
            <p className="text-[#A1A1AA] text-[11px] leading-[1.45]">{c.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-[#D4FF4A]/25 flex items-start gap-3">
        <span className="font-mono-label text-[#D4FF4A] text-[9px] shrink-0 mt-0.5">
          Confidence
        </span>
        <p className="text-[#A1A1AA] text-[11px] leading-[1.55]">
          Based on 1,361 enriched profiles / 6,075 buyers (~22%). Directionally correct. Reach
          50%+ coverage before acting on segment LTV gaps.
        </p>
      </div>
    </VisualShell>
  );
}
